/**
 * One-tap hand-off from runebound.gg and the app (owner decision Q11, docs/design/07 §2). Runebound's
 * `play-handoff` Edge Function returns `https://play.runebound.gg/<path>#token_hash=…&type=magiclink`;
 * this page exchanges the single-use token hash for its own session with
 * `supabase.auth.verifyOtp({ token_hash, type: 'magiclink' })`, then continues to the target (usually
 * `/play?deck=<id>`). A fragment keeps the token out of server logs and the Referer header. The older
 * `#th=…&deck=…&next=…` form (`@play/protocol` `parseHandoffFragment`) is accepted too.
 */
import { parseHandoffFragment } from '@play/protocol';

/** A parsed hand-off: the token hash and where to go afterwards. */
export interface Handoff {
  tokenHash: string;
  /** Same-site path to continue to (`/play?deck=…` by default). */
  next: string;
}

const SAFE_PATH = /^\/(?!\/)[^\s\\]*$/;

/** A same-site path or `null` (no scheme, no `//`, no backslashes). */
export function safeNext(path: string | null | undefined): string | null {
  return path && path.length <= 300 && SAFE_PATH.test(path) ? path : null;
}

/**
 * Reads a hand-off from a URL's hash and search. `pathname` is where the browser landed: when it is not
 * the hand-off route itself, the target is that same page (minus the fragment).
 */
export function parseHandoff(hash: string, search: string, pathname: string): Handoff | null {
  const fragment = hash.startsWith('#') ? hash.slice(1) : hash;
  if (!fragment) return null;
  const params = new URLSearchParams(fragment);
  const query = new URLSearchParams(search);
  const deck = query.get('deck') ?? params.get('deck');
  const explicitNext = safeNext(query.get('next') ?? params.get('next'));
  const landing = pathname === '/auth/handoff' ? null : safeNext(pathname + search);
  const fallback = deck ? `/play?deck=${encodeURIComponent(deck)}` : '/play';

  const tokenHash = params.get('token_hash');
  const type = params.get('type');
  if (tokenHash && tokenHash.length >= 16 && tokenHash.length <= 256 && (type === null || type === 'magiclink')) {
    return { tokenHash, next: explicitNext ?? landing ?? fallback };
  }
  const legacy = parseHandoffFragment(fragment);
  if (legacy) {
    const legacyDeck = legacy.deck ?? deck;
    return {
      tokenHash: legacy.th,
      next: safeNext(legacy.next) ?? landing ?? (legacyDeck ? `/play?deck=${encodeURIComponent(legacyDeck)}` : '/play'),
    };
  }
  return null;
}
